import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UploadSimple, Sparkle, WarningCircle, X, Camera } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAiFileParse } from '@/hooks/useAiFileParse';
import { confidenceRing } from '@/components/upload/confidence';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequisitionFileUploadDialog = ({ open, onOpenChange, onSuccess, projectCodes }) => {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const { uploading, parse } = useAiFileParse('/requisitions/parse-file');

  const [step, setStep] = useState('upload'); // upload | review
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [defaultProjectCode, setDefaultProjectCode] = useState('');

  // Use project codes from parse response if available (source of truth), else fallback prop
  const [parsedProjectCodes, setParsedProjectCodes] = useState([]);
  const codeOptions = parsedProjectCodes.length ? parsedProjectCodes : (projectCodes || []);

  const resetAll = () => {
    setStep('upload');
    setSaving(false);
    setItems([]);
    setDefaultProjectCode('');
    setParsedProjectCodes([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) resetAll();
    onOpenChange(nextOpen);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await parse(file);
    if (!data) return;
    setParsedProjectCodes(data.project_codes || []);
    setItems(
      (data.items || []).map((it, idx) => ({
        key: `req-${idx}`,
        include: true,
        reagent_name: it.reagent_name || '',
        vendor_name: it.vendor_name || '',
        catalogue_number: it.catalogue_number || '',
        quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
        project_code_id: '',
        confidence: it.confidence || {},
      }))
    );
    setStep('review');
    toast.success(`Extracted ${data.items?.length || 0} reagent(s)`);
  };

  const updateItem = (key, patch) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };

  const applyProjectToAll = (pcId) => {
    setDefaultProjectCode(pcId);
    setItems((prev) => prev.map((it) => ({ ...it, project_code_id: pcId })));
  };

  const handleSave = async () => {
    const toSave = items.filter((it) => it.include);
    if (toSave.length === 0) {
      toast.error('Select at least one item to save');
      return;
    }
    for (const it of toSave) {
      if (!it.reagent_name.trim() || !it.vendor_name.trim() || !it.catalogue_number.trim()) {
        toast.error(`Row "${it.reagent_name || 'unknown'}" needs reagent, vendor, and catalogue #`);
        return;
      }
      if (!it.project_code_id) {
        toast.error(`Row "${it.reagent_name}" needs a project code`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        requisitions: toSave.map((it) => ({
          reagent_name: it.reagent_name.trim(),
          vendor_name: it.vendor_name.trim(),
          catalogue_number: it.catalogue_number.trim(),
          quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
          project_code_id: it.project_code_id,
        })),
      };
      const res = await axios.post(`${API}/requisitions/bulk`, payload, {
        headers: getAuthHeader(),
      });
      toast.success(`Created ${res.data.length} requisition(s)`);
      resetAll();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save requisitions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="req-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={22} weight="duotone" className="text-accent-brand" />
            Import Requisitions from File
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="py-8">
            <div className="border-2 border-dashed border-border-default rounded-lg p-10 text-center bg-surface">
              <UploadSimple size={40} className="text-text-tertiary mx-auto mb-3" weight="duotone" />
              <p className="text-text-primary font-medium mb-1">
                Upload a PDF, image, or screenshot of the reagents you need
              </p>
              <p className="text-sm text-text-tertiary mb-6">
                Our AI will read the file and create a batch of requisitions for you to review.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp,image/heic"
                className="hidden"
                onChange={handleFileSelected}
                data-testid="req-file-input"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileSelected}
                data-testid="req-camera-input"
              />
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                  data-testid="choose-req-file-button"
                >
                  {uploading ? 'Reading file (10–30s)…' : 'Choose file'}
                </Button>
                <Button
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                  data-testid="take-photo-button"
                >
                  <Camera size={18} className="mr-2" weight="bold" />
                  Take Photo
                </Button>
              </div>
              <p className="text-xs text-text-tertiary mt-4">
                Supports PDF, PNG, JPG, WEBP, HEIC · Max 15 MB · Camera works on mobile devices
              </p>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-5">
            <div className="p-4 bg-surface rounded flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <p className="text-sm text-text-secondary mb-2">Apply one project code to every row (optional):</p>
                <Select value={defaultProjectCode} onValueChange={applyProjectToAll}>
                  <SelectTrigger data-testid="apply-project-all">
                    <SelectValue placeholder="Choose project code" />
                  </SelectTrigger>
                  <SelectContent>
                    {codeOptions.map((pc) => (
                      <SelectItem key={pc.id} value={pc.id}>{pc.code} — {pc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-text-tertiary">Amber fields have low AI confidence — please verify.</p>
            </div>

            <div className="border border-border-default rounded overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface border-b border-border-default">
                  <tr>
                    <th className="p-3 text-left w-10">Save</th>
                    <th className="p-3 text-left">Reagent</th>
                    <th className="p-3 text-left">Vendor</th>
                    <th className="p-3 text-left">Catalogue #</th>
                    <th className="p-3 text-left w-20">Qty</th>
                    <th className="p-3 text-left">Project Code</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.key} className="border-b border-border-default align-top" data-testid={`req-row-${it.key}`}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={it.include}
                          onChange={(e) => updateItem(it.key, { include: e.target.checked })}
                          className="w-4 h-4"
                          data-testid={`req-include-${it.key}`}
                        />
                      </td>
                      <td className="p-3">
                        <Input
                          value={it.reagent_name}
                          onChange={(e) => updateItem(it.key, { reagent_name: e.target.value })}
                          className={`h-8 ${confidenceRing(it.confidence?.reagent_name)}`}
                          placeholder="Reagent name"
                          data-testid={`req-reagent-${it.key}`}
                        />
                      </td>
                      <td className="p-3">
                        <Input
                          value={it.vendor_name}
                          onChange={(e) => updateItem(it.key, { vendor_name: e.target.value })}
                          className={`h-8 ${confidenceRing(it.confidence?.vendor_name)}`}
                          placeholder="Vendor"
                          data-testid={`req-vendor-${it.key}`}
                        />
                      </td>
                      <td className="p-3">
                        <Input
                          value={it.catalogue_number}
                          onChange={(e) => updateItem(it.key, { catalogue_number: e.target.value })}
                          className={`h-8 font-mono text-xs ${confidenceRing(it.confidence?.catalogue_number)}`}
                          placeholder="Catalogue #"
                          data-testid={`req-cat-${it.key}`}
                        />
                      </td>
                      <td className="p-3">
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={it.quantity}
                          onChange={(e) => updateItem(it.key, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                          className="h-8 w-16 text-right"
                          data-testid={`req-qty-${it.key}`}
                        />
                      </td>
                      <td className="p-3 min-w-[200px]">
                        <Select
                          value={it.project_code_id || ''}
                          onValueChange={(v) => updateItem(it.key, { project_code_id: v })}
                        >
                          <SelectTrigger
                            className={!it.project_code_id ? 'ring-2 ring-amber-400 border-amber-400' : ''}
                            data-testid={`req-project-${it.key}`}
                          >
                            <SelectValue placeholder="Select project" />
                          </SelectTrigger>
                          <SelectContent>
                            {codeOptions.map((pc) => (
                              <SelectItem key={pc.id} value={pc.id}>{pc.code} — {pc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!it.project_code_id && (
                          <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                            <WarningCircle size={12} weight="fill" /> Required
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan="5" className="p-6 text-center text-text-tertiary">
                        No reagents detected in this file.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <Button variant="outline" onClick={resetAll} data-testid="req-upload-another">
                <X size={16} className="mr-1" /> Upload another file
              </Button>
              <div className="flex items-center gap-3">
                <p className="text-sm text-text-tertiary">
                  {items.filter((i) => i.include && i.project_code_id).length} of {items.length} ready to create
                </p>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                  data-testid="save-requisitions-btn"
                >
                  {saving ? 'Creating…' : 'Create Requisitions'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RequisitionFileUploadDialog;
